import {
  useCallback,
  useEffect,
  useRef,
  type RefObject,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { Clip } from "@softmax/avatar/character";
import { readMountedElement } from "../read-mounted-element";
import { glideKeyframes, reducedMotion } from "./widget-motion";

const snap = { duration: 520, easing: "cubic-bezier(0.22, 1, 0.36, 1)" };

export function useWidgetGlide({
  root,
  dock,
  setClip,
}: {
  readonly root: RefObject<HTMLDivElement | null>;
  readonly dock: RefObject<HTMLDivElement | null>;
  readonly setClip: Dispatch<SetStateAction<Clip | null>>;
}) {
  const glideAnimation = useRef<Animation | null>(null);
  useEffect(() => () => glideAnimation.current?.cancel(), []);
  const glide = useCallback(
    (from: DOMRect) => {
      const el = readMountedElement(root);
      glideAnimation.current?.cancel();
      el.style.translate = "";
      const to = readMountedElement(dock).getBoundingClientRect();
      const dx = from.left - to.left;
      const dy = from.top - to.top;
      const far = Math.hypot(dx, dy);

      if (reducedMotion() || far < 1) {
        setClip((current) =>
          current === "fall" ? (reducedMotion() ? null : "land") : current,
        );
        return;
      }

      const animation = el.animate(
        glideKeyframes({ dx, dy, distance: far }),
        snap,
      );
      glideAnimation.current = animation;
      setClip("fall");
      const landing = window.setTimeout(
        () => setClip((current) => (current === "fall" ? "land" : current)),
        snap.duration * 0.3,
      );
      animation.addEventListener("cancel", () => window.clearTimeout(landing), {
        once: true,
      });
      animation.addEventListener(
        "finish",
        () => {
          window.clearTimeout(landing);
          glideAnimation.current = null;
          setClip((current) => (current === "fall" ? "land" : current));
        },
        { once: true },
      );
    },
    [root, dock, setClip],
  );
  return { glide, glideAnimation };
}
